/**
 * `diagram/react/shapes.tsx` — one source of truth for flowchart shape geometry
 * (interactive-diagrams v0.3).
 *
 * `shapePrims(shape, w, h)` returns the SVG primitives that draw a shape in a `0..w × 0..h` box.
 * The SAME geometry drives three surfaces so they can never disagree:
 *   - the canvas tile background (`ShapeSvg`, a React node behind the label),
 *   - the shape-menu glyphs (`ShapeGlyph`),
 *   - the positioned SVG export (`primToSvgString`, in `exportSvg.ts`).
 *
 * This replaces the old CSS-approximation shapes (which read as rectangles at small sizes and
 * couldn't render parallelograms / trapezoids / cylinders at all).
 */

import type { NodeShape } from '../model';

/** The grouped shape menu — every shape the parser/serializer round-trips (model.ts `NodeShape`). */
export const SHAPE_MENU: ReadonlyArray<{ group: string; items: ReadonlyArray<{ shape: NodeShape; label: string }> }> = [
  {
    group: 'Basic',
    items: [
      { shape: 'rectangle', label: 'Process' },
      { shape: 'rounded', label: 'Rounded' },
      { shape: 'stadium', label: 'Terminator' },
      { shape: 'circle', label: 'Start / End' },
    ],
  },
  {
    group: 'Flow',
    items: [
      { shape: 'rhombus', label: 'Decision' },
      { shape: 'hexagon', label: 'Preparation' },
      { shape: 'parallelogram', label: 'Input / Output' },
      { shape: 'parallelogram_alt', label: 'Output / Input' },
      { shape: 'trapezoid', label: 'Manual op' },
      { shape: 'trapezoid_alt', label: 'Manual input' },
    ],
  },
  {
    group: 'Data',
    items: [
      { shape: 'cylinder', label: 'Database' },
      { shape: 'subroutine', label: 'Subroutine' },
      { shape: 'asymmetric', label: 'Flag' },
    ],
  },
];

/** Shapes whose text area is narrower than the bounding box — the label gets extra padding. */
export const INSET_SHAPES = new Set<NodeShape>([
  'rhombus',
  'hexagon',
  'parallelogram',
  'parallelogram_alt',
  'trapezoid',
  'trapezoid_alt',
]);

export type ShapePrim =
  | { el: 'rect'; x: number; y: number; w: number; h: number; rx: number }
  | { el: 'ellipse'; cx: number; cy: number; rx: number; ry: number }
  | { el: 'polygon'; points: string }
  | { el: 'path'; d: string };

const r = (n: number) => Math.round(n * 100) / 100;

/**
 * The SVG primitives for a shape inside a `0..w × 0..h` box: a `body` (filled + stroked outline)
 * and zero or more `decor` primitives (stroked only — the cylinder rim, the subroutine bars).
 * A small pad keeps the stroke off the edge so it isn't half-clipped.
 */
export function shapePrims(shape: NodeShape, w: number, h: number): { body: ShapePrim; decor: ShapePrim[] } {
  const pad = 2;
  const x0 = pad;
  const y0 = pad;
  const x1 = w - pad;
  const y1 = h - pad;
  const cw = x1 - x0;
  const ch = y1 - y0;
  const cx = w / 2;
  const cy = h / 2;
  const poly = (pts: Array<[number, number]>): ShapePrim => ({
    el: 'polygon',
    points: pts.map(([px, py]) => `${r(px)},${r(py)}`).join(' '),
  });

  switch (shape) {
    case 'rounded':
      return { body: { el: 'rect', x: x0, y: y0, w: cw, h: ch, rx: Math.min(16, ch * 0.35) }, decor: [] };
    case 'stadium':
      return { body: { el: 'rect', x: x0, y: y0, w: cw, h: ch, rx: ch / 2 }, decor: [] };
    case 'circle':
      return { body: { el: 'ellipse', cx, cy, rx: cw / 2, ry: ch / 2 }, decor: [] };
    case 'rhombus':
      return { body: poly([[cx, y0], [x1, cy], [cx, y1], [x0, cy]]), decor: [] };
    case 'hexagon':
      return {
        body: poly([
          [x0 + cw * 0.2, y0],
          [x1 - cw * 0.2, y0],
          [x1, cy],
          [x1 - cw * 0.2, y1],
          [x0 + cw * 0.2, y1],
          [x0, cy],
        ]),
        decor: [],
      };
    case 'parallelogram':
      return { body: poly([[x0 + cw * 0.22, y0], [x1, y0], [x1 - cw * 0.22, y1], [x0, y1]]), decor: [] };
    case 'parallelogram_alt':
      return { body: poly([[x0, y0], [x1 - cw * 0.22, y0], [x1, y1], [x0 + cw * 0.22, y1]]), decor: [] };
    case 'trapezoid':
      return { body: poly([[x0 + cw * 0.22, y0], [x1 - cw * 0.22, y0], [x1, y1], [x0, y1]]), decor: [] };
    case 'trapezoid_alt':
      return { body: poly([[x0, y0], [x1, y0], [x1 - cw * 0.22, y1], [x0 + cw * 0.22, y1]]), decor: [] };
    case 'asymmetric':
      return { body: poly([[x0, y0], [x0 + cw * 0.82, y0], [x1, cy], [x0 + cw * 0.82, y1], [x0, y1]]), decor: [] };
    case 'subroutine':
      return {
        body: { el: 'rect', x: x0, y: y0, w: cw, h: ch, rx: 3 },
        decor: [
          { el: 'path', d: `M${r(x0 + 7)},${r(y0)} L${r(x0 + 7)},${r(y1)}` },
          { el: 'path', d: `M${r(x1 - 7)},${r(y0)} L${r(x1 - 7)},${r(y1)}` },
        ],
      };
    case 'cylinder': {
      const e = Math.min(9, ch * 0.16);
      return {
        body: {
          el: 'path',
          d:
            `M${r(x0)},${r(y0 + e)} A ${r(cw / 2)} ${r(e)} 0 0 1 ${r(x1)} ${r(y0 + e)} ` +
            `L${r(x1)},${r(y1 - e)} A ${r(cw / 2)} ${r(e)} 0 0 1 ${r(x0)} ${r(y1 - e)} Z`,
        },
        decor: [{ el: 'path', d: `M${r(x0)},${r(y0 + e)} A ${r(cw / 2)} ${r(e)} 0 0 0 ${r(x1)} ${r(y0 + e)}` }],
      };
    }
    case 'rectangle':
    case 'none':
    default:
      return { body: { el: 'rect', x: x0, y: y0, w: cw, h: ch, rx: 4 }, decor: [] };
  }
}

// ── SVG string emitter (used by exportSvg.ts) ────────────────────────────────

/** Serialize one primitive to an SVG element string with the given fill/stroke. */
export function primToSvgString(p: ShapePrim, fill: string, stroke: string, strokeWidth: number): string {
  const paint = `fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"`;
  switch (p.el) {
    case 'rect':
      return `<rect x="${p.x}" y="${p.y}" width="${p.w}" height="${p.h}" rx="${p.rx}" ${paint}/>`;
    case 'ellipse':
      return `<ellipse cx="${p.cx}" cy="${p.cy}" rx="${p.rx}" ry="${p.ry}" ${paint}/>`;
    case 'polygon':
      return `<polygon points="${p.points}" ${paint}/>`;
    case 'path':
      return `<path d="${p.d}" ${paint}/>`;
  }
}

// ── React renderers ──────────────────────────────────────────────────────────

function PrimEl({
  p,
  fill,
  stroke,
  strokeWidth,
}: {
  p: ShapePrim;
  fill: string;
  stroke: string;
  strokeWidth: number;
}): JSX.Element {
  const common = { fill, stroke, strokeWidth, vectorEffect: 'non-scaling-stroke' as const };
  switch (p.el) {
    case 'rect':
      return <rect x={p.x} y={p.y} width={p.w} height={p.h} rx={p.rx} {...common} />;
    case 'ellipse':
      return <ellipse cx={p.cx} cy={p.cy} rx={p.rx} ry={p.ry} {...common} />;
    case 'polygon':
      return <polygon points={p.points} {...common} />;
    case 'path':
      return <path d={p.d} {...common} />;
  }
}

/**
 * The tile background — an SVG that fills the node and draws the shape. `preserveAspectRatio="none"`
 * stretches the 100×100 geometry to the node's real size; `non-scaling-stroke` keeps the outline
 * an even width regardless. Fill/stroke default to theme tokens and are overridden by the node's
 * native mermaid `style` (portable hexes).
 */
export function ShapeSvg({
  shape,
  fill = 'var(--bg-1)',
  stroke = 'var(--primary)',
  strokeWidth = 1.5,
}: {
  shape: NodeShape;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
}): JSX.Element {
  const { body, decor } = shapePrims(shape, 100, 100);
  return (
    <svg className="diagram-node-shape" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
      <PrimEl p={body} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />
      {decor.map((d, i) => (
        <PrimEl key={i} p={d} fill="none" stroke={stroke} strokeWidth={strokeWidth} />
      ))}
    </svg>
  );
}

/** The shape-menu glyph — the same geometry at glyph size, drawn in `currentColor`. */
export function ShapeGlyph({ shape }: { shape: NodeShape }): JSX.Element {
  const { body, decor } = shapePrims(shape, 100, 72);
  return (
    <svg className="dg-shape-glyph" viewBox="0 0 100 72" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      <PrimEl p={body} fill="currentColor" stroke="currentColor" strokeWidth={6} />
      {decor.map((d, i) => (
        <PrimEl key={i} p={d} fill="none" stroke="currentColor" strokeWidth={6} />
      ))}
    </svg>
  );
}
